import isNil from 'lodash/isNil';
import React, { useMemo, FocusEvent } from 'react';
import { useTranslation } from 'react-i18next';
import CategoryColorIndicator from '~/components/data-display/CategoryColorIndicator/CategoryColorIndicator';
import ComboBox, { BaseComboBoxProps, ComboBoxOption } from '~/components/inputs/Select/ComboBox';
import { useCategoriesQuery } from '~/services/category';
import { Category, Subcategory } from '~/types/category';
import { sortAlphabetically } from '~/utils/sort';

interface CategoryComboBoxProps extends BaseComboBoxProps {
  categoryId: Category['id'] | null | undefined;
  subcategoryId?: Subcategory['id'] | null | undefined;
  inputRef?: React.RefObject<HTMLDivElement>;
  onFocus?: (e: FocusEvent<Element, Element>) => void;
  onChange: (
    categoryId: Category['id'] | null | undefined,
    subcategoryId: Subcategory['id'] | null | undefined,
  ) => void;
  excludeSubcategories?: boolean;
  excludeUncategorized?: boolean;
}

interface CategoryValue {
  categoryId: Category['id'] | null | undefined;
  subcategoryId: Subcategory['id'] | null | undefined;
}

const categoriesToOptions = (categories: Category[], excludeUncategorized: boolean): ComboBoxOption<CategoryValue>[] =>
  categories.reduce((acc, category) => {
    acc.push({
      id: category.id,
      value: {
        categoryId: category.id,
        subcategoryId: null,
      },
      media: <CategoryColorIndicator categoryColor={category.color} size="small" />,
      label: category.name,
    });

    if (!excludeUncategorized) {
      category.subcategories?.forEach((subCat) => {
        acc.push({
          id: subCat.id,
          value: {
            categoryId: category.id,
            subcategoryId: subCat.id,
          },
          media: <CategoryColorIndicator categoryColor={category.color} size="small" />,
          label: [category.name, subCat.name].join(' / '),
        });
      });
    }
    return acc;
  }, [] as ComboBoxOption<CategoryValue>[]);

const CategoryComboBox: React.FC<CategoryComboBoxProps> = ({
  categoryId,
  subcategoryId,
  onChange,
  onFocus,
  excludeSubcategories,
  excludeUncategorized,
  inputRef,
  ...props
}) => {
  const { t } = useTranslation('categorization');

  const categoriesQuery = useCategoriesQuery();
  const options = useMemo(() => {
    const convertedOptions = categoriesToOptions(categoriesQuery.data || [], Boolean(excludeSubcategories));
    const sortedOptions = convertedOptions.sort((el1, el2) => sortAlphabetically(el1.label, el2.label));

    if (excludeUncategorized) {
      return sortedOptions;
    }

    const uncategorisedOption: ComboBoxOption<CategoryValue> = {
      label: t('uncategorized'),
      id: 'uncategorized',
      media: <CategoryColorIndicator size="small" />,
      value: {
        categoryId: null,
        subcategoryId: null,
      },
    };

    return [uncategorisedOption].concat(sortedOptions);
  }, [categoriesQuery.data, t, excludeSubcategories, excludeUncategorized]);

  const valueToId = (value: CategoryValue | null): string => {
    if (isNil(value?.categoryId)) {
      return 'uncategorized';
    }
    return value.subcategoryId ?? value.categoryId;
  };

  return (
    <ComboBox<CategoryValue>
      {...props}
      ref={inputRef}
      menuTrigger="focus"
      options={options}
      value={{ categoryId, subcategoryId }}
      onFocus={onFocus}
      onChange={(value) => {
        onChange(value?.categoryId, value?.subcategoryId);
      }}
      valueToId={valueToId}
    />
  );
};

export default CategoryComboBox;
